import { z } from "zod"

export interface DynamoQueryResponse<T> {
  response: T
  newToken?: string
}

export const InteractionOutcomeSchema = z.enum(["voicemail", "missed", "failed", "cancelled", "success", "emailDelivered", "emailOpened", "emailClicked", "emailReplied", "transferred", "unknown"])
export type TInteractionOutcome = z.infer<typeof InteractionOutcomeSchema>

export const InteractionTypeSchema = z.enum(["message", "call", "liveCall", "email", "video", "unknown"])
export type TInteractionType = z.infer<typeof InteractionTypeSchema>

export const RawInteractionTypeSchema = z.enum(["message", "email", "departamentCall", "personalCall", "liveCall", "video"])
export type TRawInteractionType = z.infer<typeof RawInteractionTypeSchema>

export const InteractionSchema = z
  .object({
    CallRecordingUploaded: z.boolean().nullish(),
    VoicemailUploaded: z.boolean().nullish(),
    Hash: z.string().nullish(),
    AccountId: z.string(),
    Body: z.string().nullish(),
    MediaUrls: z.string().array().nullish(),
    Status: z.string().nullish(),
    CallRecordingHash: z.string().nullish(),
    CallRecordingId: z.string().nullish(),
    CallerId: z.string().nullish(),
    CompanyName: z.string().nullish(),
    ContactCompanyName: z.string().nullish(),
    ContactEmails: z.string().nullish(),
    ContactFirstname: z.string().nullish(),
    ContactLastNote: z.string().nullish(),
    ContactFullname: z.string().nullish(),
    ContactLastname: z.string().nullish(),
    ContactPersonId: z.string().nullish(),
    ContactPhoneNumber: z.string().nullish(),
    ContactPhoneNumbers: z.string().nullish(),
    ContactRemoteJson: z.string().nullish(),
    Delivered: z.boolean().nullish(),
    Clicked: z.boolean().nullish(),
    Opened: z.boolean().nullish(),
    Replied: z.boolean().nullish(),
    Departments: z.any().nullish(),
    Direction: z.enum(["in", "ou"]).nullish(),
    DirectionTimestamp: z.string().nullish(),
    DispositionLabel: z.string().nullish(),
    DispositionTimestamp: z.string().nullish(),
    DurationInMillis: z.number().int().nullish(),
    DurationInSecs: z.number().int().nullish(),
    DurationTimestamp: z.string().nullish(),
    FlagTag: z.string().nullish(),
    HasCallQualityWarnings: z.boolean().nullish(),
    HasExitedQueue: z.boolean().nullish(),
    InstaCallId: z.number().int().nullish(),
    IsAbandoned: z.boolean().nullish(),
    IsBusinessHours: z.boolean().nullish(),
    IsChecked: z.boolean().nullish(),
    IsExternalTransfer: z.boolean().nullish(),
    IsLocalPresence: z.boolean().nullish(),
    IsPrivateCall: z.boolean().nullish(),
    NeedsMyAttention: z.number().int().nullish(),
    Origin: z.string().nullish(),
    Outcome: z.string().nullish(),
    OutcomeTimestamp: z.string().nullish(),
    RemoteJson: z.string().nullish(),
    RingId: z.string().nullish(),
    RingoNumber: z.string().nullish(),
    RingoNumberTimestamp: z.string().nullish(),
    SessionId: z.string().nullish(),
    StartDatetime: z.string().nullish(),
    StartTimestamp: z.number().int().nullish(),
    TTL: z.number().int().nullish(),
    TimeTalk: z.number().int().nullish(),
    TimeZone: z.string().nullish(),
    Type: z.string().nullish(),
    TypeTimestamp: z.string().nullish(),
    UserCallerIdType: z.string().nullish(),
    UserFullname: z.string().nullish(),
    UserPersonId: z.string().nullish(),
    UserPhoneNumber: z.string().nullish(),
    VoicemailHash: z.string().nullish(),
    VoicemailId: z.string().nullish(),
    Note: z.string().nullish(),
    From: z.string().nullish(),
    To: z.string().nullish(),
    Subject: z.string().nullish(),
    TranscriptUrls: z.string().nullish(),
    TranscriptSummary: z.string().nullish(),
    AnsweredBy: z.string().nullish(),
  })
  .passthrough()

export type TInteraction = z.infer<typeof InteractionSchema>

export const ContactSchema = InteractionSchema.extend({
  ContactPhoneNumber: z.string().nullish(),
  ContactEmail: z.string().nullish(),
  ContactPersonId: z.string(),
  ContactLastNote: z.any().nullish(),
  ContactRemoteJson: z.string().nullish(),
  SearchTerm: z.string(),
  TTL: z.number().int(),
  ContactEmails: z.any().nullish(),
  ContactLastDisposition: z.any().nullish(),
  LastTimestamp: z.number().int(),
  ContactPhoneNumbers: z.any().nullish(),
  ContactCompanyName: z.string(),
  AccountId: z.string(),
  ContactDNC: z.boolean(),
  ContactFullname: z.string(),
  Occurrences: z.number().int(),
  Hash: z.string().nullish(),
  Note: z.string().nullish(),
  raw: z.any().nullish(),
})

export type TContact = z.infer<typeof ContactSchema>

export const GroupedInteractionSchema = z.object({
  data: z.array(InteractionSchema),
  RingId: z.string(),
  Hash: z.string(),
})
export type TGroupedInteraction = z.infer<typeof GroupedInteractionSchema>

export const LastEvaluatedKeySchema = z.any().optional().nullish()
// z.object({
//   StartTimestamp: z.number().int().nullish(),
//   AccountId: z.string().nullish(),
//   RingId: z.string().nullish(),
//   ContactPersonId: z.string().nullish(),
// })

export type TLastEvaluatedKey = z.infer<typeof LastEvaluatedKeySchema>

export interface IDynamoQueryParams {
  lastEvaluatedKey?: TLastEvaluatedKey
  limit?: number
}

export interface IDynamoQueryResult<T> {
  Count: number
  Items: Array<T>
  ScannedCount: number
  Iterations: number
  LastEvaluatedKey?: TLastEvaluatedKey
}
